/**
 * AmountFormatter.js
 *
 * Canonical utility for formatting monetary amounts throughout the app.
 * Use this for display in templates, computed properties, and CSV exports.
 *
 * Supported formats:
 *   US    — 1,000,000.00  (US/UK)
 *   EU    — 1.000.000,00  (European)
 *   SI_US — 1 000 000.00  (SI/BIPM with decimal point)
 *   SI_EU — 1 000 000,00  (SI/BIPM with decimal comma)
 *
 * Currently defaults to US.  When localization is added, swap DEFAULT_AMOUNT_FORMAT
 * to a user/org setting retrieved from the store.
 */

export const AMOUNT_FORMAT = Object.freeze({
  US: 'US',       // 1,000,000.00
  EU: 'EU',       // 1.000.000,00
  SI_US: 'SI_US', // 1 000 000.00
  SI_EU: 'SI_EU' // 1 000 000,00
})

/** App-wide default.  Change here to switch all display formatting at once. */
export const DEFAULT_AMOUNT_FORMAT = AMOUNT_FORMAT.US

// Base Intl locale for each format (SI variants share a locale; separator is replaced post-format)
const BASE_LOCALE = {
  [AMOUNT_FORMAT.US]: 'en-US',
  [AMOUNT_FORMAT.EU]: 'de-DE',
  [AMOUNT_FORMAT.SI_US]: 'en-US', // comma thousands → replaced with space
  [AMOUNT_FORMAT.SI_EU]: 'de-DE' // period thousands → replaced with space
}

/**
 * Parses a raw value (number or formatted string) to a plain JS number.
 * Handles US (1,234.56), European (1.234,56), and SI space-separated formats,
 * with or without a decimal part, plus accounting-style negatives \u2014 e.g.
 * "1,234", "(1,234.56)" and a leading currency symbol all parse correctly.
 *
 * @param {number|string|null|undefined} value
 * @returns {number|''}  Parsed number, or '' when the input is empty/blank.
 */
export function parseAmount (value) {
  if (value === null || value === undefined || value === '') return ''
  if (typeof value === 'number') return value

  let str = String(value).trim()
  if (str === '') return ''

  // Accounting-style negative: (1,234.56) \u2192 -1,234.56
  let sign = 1
  if (/^\(.*\)$/.test(str)) {
    sign = -1
    str = str.slice(1, -1).trim()
  }

  // Strip a leading currency symbol / non-numeric prefix (e.g. "$1,234.56")
  str = str.replace(/^[^\d\s\u00A0\u202F(.,+-]+/, '').trim()
  if (str === '') return NaN

  const finalize = (n) => (isNaN(n) ? NaN : sign * n)

  // Plain numeric string (no grouping separators) \u2014 also covers "1.234" as 1.234
  if (!isNaN(str)) return finalize(parseFloat(str))

  // US      : 1,234.56 / -1,234.56 / 1,234   (comma thousands, optional dot decimal)
  if (/^-?[\d,]+(\.\d+)?$/.test(str)) {
    return finalize(parseFloat(str.replace(/,/g, '')))
  }

  // European: 1.234,56 / -1.234,56 / 1.234   (dot thousands, optional comma decimal)
  if (/^-?[\d.]+(,\d+)?$/.test(str)) {
    return finalize(parseFloat(str.replace(/\./g, '').replace(',', '.')))
  }

  // SI with spaces: 1 234.56 / 1 234,56 / 1 234 (regular or non-breaking space)
  if (/^-?[\d\s\u00A0\u202F]+([.,]\d+)?$/.test(str)) {
    return finalize(parseFloat(str.replace(/[\s\u00A0\u202F]/g, '').replace(',', '.')))
  }

  return NaN
}

/**
 * Formats an amount for display.
 *
 * Right-align in templates by wrapping the output in a class with `text-align: right`.
 * For AG Grid columns use agGridAmountValueFormatter from AgGridAmountFormatter.js instead.
 *
 * @param {number|string|null|undefined} value
 * @param {string}  [format=DEFAULT_AMOUNT_FORMAT]  One of the AMOUNT_FORMAT constants.
 * @param {number}  [decimals=2]                    Number of decimal places.
 * @param {boolean} [useParenthesesForNegative=false]
 * @returns {string}
 */
export function formatAmount (value, format = DEFAULT_AMOUNT_FORMAT, decimals = 2, useParenthesesForNegative = false) {
  const num = typeof value === 'number' ? value : parseAmount(value)
  if (num === '' || isNaN(num)) return ''

  const locale = BASE_LOCALE[format] || 'en-US'
  const options = {
    style: 'decimal',
    useGrouping: true,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }

  let result = new Intl.NumberFormat(locale, options).format(num)

  // Normalize negative zero produced by rounding (e.g. -0.004 with 2 decimals)
  if (/^-0[.,]?0*$/.test(result)) {
    result = new Intl.NumberFormat(locale, options).format(0)
  }

  // SI formats: replace the thousands separator character with a plain space
  if (format === AMOUNT_FORMAT.SI_US) {
    // en-US thousands separator is ','
    result = result.replace(/,/g, ' ')
  } else if (format === AMOUNT_FORMAT.SI_EU) {
    // de-DE thousands separator is '.'  (decimal is ',', so all '.' are safe to replace)
    result = result.replace(/\./g, ' ')
  }

  if (useParenthesesForNegative && result.startsWith('-')) {
    result = '(' + result.slice(1) + ')'
  }

  return result
}

/**
 * Formats an amount for CSV export.
 *
 * US standard with NO thousands separator — only the decimal point, regardless
 * of the app's display locale:  10000 → 10000.00
 *
 * Grouping separators are intentionally omitted so the value stays a single,
 * machine-parseable CSV field (a thousands comma would split it across columns,
 * a localized decimal comma would corrupt it).
 *
 * @param {number|string|null|undefined} value
 * @param {number} [decimals=2]
 * @returns {string}
 */
export function formatAmountForCSV (value, decimals = 2) {
  const num = typeof value === 'number' ? value : parseAmount(value)
  if (num === '' || isNaN(num)) return ''

  const options = {
    style: 'decimal',
    useGrouping: false,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }

  let result = new Intl.NumberFormat('en-US', options).format(num)

  if (/^-0[.,]?0*$/.test(result)) {
    result = new Intl.NumberFormat('en-US', options).format(0)
  }

  return result
}



// WEBPACK FOOTER //
// ./src/utils/helpers/AmountFormatter.js